/**
 * useMessage — antd message/notification/Modal.confirm 래퍼 훅.
 *
 * 【역할】
 * - 백엔드 cm_message 테이블의 MSG_XXX 코드를 레벨(S/I/W/E/Q) 에 맞게 토스트/Modal 로 표출.
 * - 프런트 분기 코드가 "성공/실패/확인" 메시지를 하드코딩하지 않도록 중앙화.
 *
 * 【주요 기능】
 * - success/info/warn/error(text): 즉석 토스트
 * - byId(code, ...args): MSG_XXX 조회 + {0}/{1} placeholder 치환 후 레벨별 토스트
 * - confirm(text, onOk) / confirmById(code, onOk, ...args): Modal.confirm 래핑
 * - message/notification/modal 원본 인스턴스 노출 (커스텀 용도)
 *
 * 【내부 상태 / 캐시 구조】
 * - messageCache: Map<`${locale}:${code}`, FwMessage> — locale 별 분리 저장.
 * - LOCALE_CHANGE_EVENT 수신 시 캐시 전체 clear → 다음 요청 시 새 locale 로 재조회.
 *
 * 【사용 예시】
 * ```tsx
 * const msg = useMessage()
 * await msg.byId('MSG_SAVE_SUCCESS')
 * msg.confirmById('MSG_DELETE_CONFIRM', async () => { await del() })
 * ```
 *
 * 【왜 이 패턴?】
 * - vue-spring-fw 원본의 msg composable 포팅 — 기업 프런트의 "메시지 DB 화" 관행 유지.
 * - 현재 locale 을 `window.localStorage.fw.locale` 에서 읽어 서버 요청이 즉시 다국어 지원.
 */
import { useCallback, useMemo } from 'react'
import { useFrameworkContext } from './FrameworkProvider'
import { useLabel } from './useLabel'
import { LOCALE_CHANGE_EVENT } from './useLocale'
import http from './api/axios'

/** 백엔드 cm_message 레코드의 프런트 매핑 — level=S/I/W/E/Q. */
interface FwMessage {
  code: string
  level: 'S' | 'I' | 'W' | 'E' | 'Q'
  text: string
}

// locale 변경 시 자동 invalidate — key=`${locale}:${code}` 로 분리 저장.
// 하위 호환: LOCALE_CHANGE_EVENT 리스너가 전체 캐시 clear.
const messageCache: Map<string, FwMessage> = new Map()

if (typeof window !== 'undefined') {
  window.addEventListener(LOCALE_CHANGE_EVENT, () => {
    messageCache.clear()
  })
}

/** 현재 UI locale — localStorage 우선, SSR 안전 fallback 'ko'. */
function currentLocale(): string {
  if (typeof window === 'undefined') return 'ko'
  return window.localStorage.getItem('fw.locale') || 'ko'
}

/**
 * 메시지 1건 로드 — 캐시 hit 시 즉시 반환, miss 시 /common/messages/{code} 호출.
 * 실패 시 code 자체를 text 로 가진 fallback 메시지 캐시 (재호출 폭주 방지).
 */
async function loadMessage(code: string): Promise<FwMessage> {
  const key = `${currentLocale()}:${code}`
  const cached = messageCache.get(key)
  if (cached) return cached
  try {
    const res = await http.get(`/common/messages/${code}`)
    const m: FwMessage = res.data?.data ?? res.data ?? { code, level: 'I', text: code }
    messageCache.set(key, m)
    return m
  } catch {
    const fallback: FwMessage = { code, level: 'I', text: code }
    messageCache.set(key, fallback)
    return fallback
  }
}

/**
 * {0}/{1}... placeholder 치환 — args 가 없거나 인덱스가 없으면 빈 문자열.
 */
function formatText(text: string, args?: (string | number)[]): string {
  if (!args) return text
  return text.replace(/\{(\d+)\}/g, (_, i) => String(args[Number(i)] ?? ''))
}

/**
 * useMessage — 메시지 표출 API 집합.
 * @returns 토스트/confirm 메서드 + antd 원본 인스턴스.
 */
export function useMessage() {
  const { message, notification, modal } = useFrameworkContext()
  const { t } = useLabel()

  /** 성공 토스트 — 초록색, 자동 닫힘. */
  const success = useCallback((text: string) => message.success(text), [message])
  /** 정보 토스트 — 회색, 자동 닫힘. */
  const info = useCallback((text: string) => message.info(text), [message])
  /** 경고 토스트 — 노란색, 자동 닫힘. */
  const warn = useCallback((text: string) => message.warning(text), [message])
  /** 에러 토스트 — 빨간색, 자동 닫힘. */
  const error = useCallback((text: string) => message.error(text), [message])

  /**
   * MSG_XXX 코드 → 레벨별 토스트 자동 표출.
   * @param code - 백엔드 cm_message 코드 (예: MSG_SAVE_SUCCESS).
   * @param args - {0}/{1} placeholder 치환 값 (가변 인자).
   */
  const byId = useCallback(
    async (code: string, ...args: (string | number)[]) => {
      const m = await loadMessage(code)
      const text = formatText(m.text, args)
      switch (m.level) {
        case 'S':
          message.success(text)
          break
        case 'W':
          message.warning(text)
          break
        case 'E':
          message.error(text)
          break
        default:
          message.info(text)
      }
    },
    [message]
  )

  /**
   * MSG_XXX 코드 기반 confirm Modal — 본문 텍스트는 cm_message 에서 조회.
   * @param onOk - 확인 클릭 시 실행 (await 지원).
   * @param args - 본문 placeholder 치환용 가변 인자.
   */
  const confirmById = useCallback(
    async (code: string, onOk: () => void | Promise<void>, ...args: (string | number)[]) => {
      const m = await loadMessage(code)
      const text = formatText(m.text, args)
      modal.confirm({
        title: t('TTL_CONFIRM', '확인'),
        content: text,
        okText: t('BTN_CONFIRM', '확인'),
        cancelText: t('BTN_CANCEL', '취소'),
        onOk: async () => {
          await onOk()
        }
      })
    },
    [modal, t]
  )

  /**
   * 평문 텍스트 기반 confirm Modal — i18n 코드가 없는 임시 메시지용.
   * @param text - 모달 본문 텍스트 (이미 번역된 문자열).
   * @param onOk - 확인 클릭 시 실행 (await 지원).
   */
  const confirm = useCallback(
    (text: string, onOk: () => void | Promise<void>) => {
      modal.confirm({
        title: t('TTL_CONFIRM', '확인'),
        content: text,
        okText: t('BTN_CONFIRM', '확인'),
        cancelText: t('BTN_CANCEL', '취소'),
        onOk: async () => {
          await onOk()
        }
      })
    },
    [modal, t]
  )

  return useMemo(
    () => ({
      success,
      info,
      warn,
      error,
      byId,
      confirm,
      confirmById,
      message,
      notification,
      modal
    }),
    [success, info, warn, error, byId, confirm, confirmById, message, notification, modal]
  )
}

export type UseMessageResult = ReturnType<typeof useMessage>
